import React, { useEffect, useState } from 'react';
import { Calculator, ArrowRight, Info, Loader2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { getPricing, estimatePrice } from '../api/api';

const LIBELLES_FREQUENCE = {
  ponctuel: 'Une seule fois',
  mensuel: 'Une fois par mois',
  bimensuel: 'Deux fois par mois',
  hebdomadaire: 'Une fois par semaine',
  quotidien: 'Tous les jours',
};

const LIBELLES_TYPE = {
  bureaux: 'Bureaux',
  commerce: 'Commerce',
  domicile: 'Domicile',
  immeuble: "Parties communes d'immeuble",
  chantier: 'Fin de chantier',
  vitres: 'Vitrerie',
};

export const PriceEstimator = () => {
  const [reglages, setReglages] = useState(null);
  const [surface, setSurface] = useState('');
  const [frequence, setFrequence] = useState('');
  const [type, setType] = useState('');
  const [resultat, setResultat] = useState(null);
  const [calculEnCours, setCalculEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    getPricing()
      .then(setReglages)
      .catch(() => setReglages(null));
  }, []);

  // Le calcul reste côté serveur : les taux ne transitent jamais dans le navigateur.
  useEffect(() => {
    const valeur = parseFloat(surface);
    if (!valeur || valeur <= 0 || !frequence || !type) {
      setResultat(null);
      return;
    }

    let annule = false;
    setCalculEnCours(true);
    setErreur('');

    const minuteur = setTimeout(() => {
      estimatePrice({ surface_m2: valeur, frequency: frequence, property_type: type })
        .then((data) => !annule && setResultat(data))
        .catch(() => !annule && setErreur("L'estimation n'a pas pu être calculée."))
        .finally(() => !annule && setCalculEnCours(false));
    }, 400);

    return () => {
      annule = true;
      clearTimeout(minuteur);
    };
  }, [surface, frequence, type]);

  if (!reglages?.enabled) return null;

  // Message précis plutôt que générique : on nomme ce qui manque réellement.
  const manquants = [
    !parseFloat(surface) || parseFloat(surface) <= 0 ? 'la surface' : null,
    !type ? 'le type de local' : null,
    !frequence ? 'la fréquence' : null,
  ].filter(Boolean);

  const frequences = Object.keys(reglages.frequency_factors || {});
  const types = Object.keys(reglages.type_factors || {});
  const formater = (montant) =>
    new Intl.NumberFormat('fr-BE', {
      style: 'currency',
      currency: reglages.currency || 'EUR',
      maximumFractionDigits: 0,
    }).format(montant);

  return (
    <section id="estimateur" className="py-16 sm:py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Calculator className="w-4 h-4" aria-hidden="true" />
            Estimation gratuite et immédiate
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Combien coûterait votre nettoyage&nbsp;?
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
            Trois informations suffisent : le prix s'affiche aussitôt, sans bouton à
            cliquer, sans inscription et sans engagement.
          </p>
        </div>

        <Card className="border-gray-200 shadow-lg">
          <CardContent className="p-6 sm:p-8">
            <div className="grid md:grid-cols-3 gap-5">
              <div>
                <Label htmlFor="est-surface" className="text-gray-700 font-semibold">
                  Surface à nettoyer
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="est-surface"
                    type="number"
                    min="1"
                    max="100000"
                    inputMode="numeric"
                    placeholder="150"
                    value={surface}
                    onChange={(e) => setSurface(e.target.value)}
                    className="h-12 pr-12"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                    m²
                  </span>
                </div>
              </div>

              <div>
                <Label htmlFor="est-type" className="text-gray-700 font-semibold">
                  Type de local
                </Label>
                <select
                  id="est-type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="mt-2 w-full h-12 rounded-md border border-gray-200 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
                >
                  <option value="">Sélectionnez…</option>
                  {types.map((cle) => (
                    <option key={cle} value={cle}>
                      {LIBELLES_TYPE[cle] || cle}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="est-frequence" className="text-gray-700 font-semibold">
                  Fréquence
                </Label>
                <select
                  id="est-frequence"
                  value={frequence}
                  onChange={(e) => setFrequence(e.target.value)}
                  className="mt-2 w-full h-12 rounded-md border border-gray-200 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
                >
                  <option value="">Sélectionnez…</option>
                  {frequences.map((cle) => (
                    <option key={cle} value={cle}>
                      {LIBELLES_FREQUENCE[cle] || cle}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-8" aria-live="polite">
              {erreur && <p className="text-center text-red-600 text-sm">{erreur}</p>}

              {/* Les trois champs sont remplis mais le calcul n'est pas revenu :
                  sans cet état, le message « complétez les trois champs »
                  s'affichait alors qu'ils l'étaient, ce qui laissait croire à
                  un bouton d'envoi manquant. */}
              {!resultat && !erreur && calculEnCours && (
                <p className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  Calcul de votre estimation…
                </p>
              )}

              {!resultat && !erreur && !calculEnCours && (
                <p className="text-center text-gray-500 text-sm">
                  {manquants.length === 0
                    ? 'Votre estimation apparaît ici, automatiquement.'
                    : `Complétez ${
                        manquants.length > 1
                          ? `${manquants.slice(0, -1).join(', ')} et ${manquants.at(-1)}`
                          : manquants[0]
                      } pour voir votre estimation.`}
                </p>
              )}

              {resultat && (
                <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-xl p-6 text-center">
                  <p className="text-sm text-gray-600 mb-1">Estimation indicative</p>
                  <p
                    className={`text-4xl sm:text-5xl font-bold text-gray-900 transition-opacity ${
                      calculEnCours ? 'opacity-40' : 'opacity-100'
                    }`}
                  >
                    {formater(resultat.low)} – {formater(resultat.high)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    par intervention, hors TVA
                  </p>

                  <p className="flex items-start gap-2 justify-center text-xs text-gray-500 mt-4 max-w-md mx-auto text-left">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{resultat.disclaimer}</span>
                  </p>

                  <a
                    href="#quote"
                    className="mt-6 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    Obtenir un devis précis
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </a>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default PriceEstimator;
